import { IconArrowRight, IconArticle } from "@tabler/icons-react"
import { type FC } from "react"

import type {
	BlogPostsBlockContent,
	LandingPageBlogPostSummary,
	LandingPageBlogPostsBlock,
} from "@forento/core/models/landing"
import { cn } from "@forento/ui/utilities/tailwind"

import { usePlatform } from "~/contexts/UserContext"
import { query } from "~/utilities/trpc"

/** Published posts, newest first, as the website lists them. Undefined while loading. */
export function usePublishedBlogPosts(): LandingPageBlogPostSummary[] | undefined {
	const { data } = query.landing.blog.list.useQuery(undefined, { refetchOnWindowFocus: false })
	if (data === undefined) return undefined
	if (data.status !== "ok") return []

	const now = new Date()
	return data.posts
		.filter(post => post.publishDate !== null && post.publishDate <= now)
		.toSorted((a, b) => b.publishDate!.getTime() - a.publishDate!.getTime())
}

export function getBlogPostsBlockPosts(
	posts: LandingPageBlogPostSummary[],
	content: BlogPostsBlockContent,
): LandingPageBlogPostSummary[] {
	const tag = content.tag?.toLowerCase()
	return posts
		.filter(post => tag === undefined || post.tags.some(postTag => postTag.toLowerCase() === tag))
		.slice(0, content.postCount)
}

type Props = { block: LandingPageBlogPostsBlock }
const BlogPostsBlockPreview: FC<Props> = ({ block }) => {
	const platform = usePlatform()!
	const publishedPosts = usePublishedBlogPosts()
	const { title, layout, postCount } = block.content
	const posts = publishedPosts === undefined ? [] : getBlogPostsBlockPosts(publishedPosts, block.content)
	// Placeholders stand in until the creator publishes a matching post, which the website would otherwise hide.
	const items: (LandingPageBlogPostSummary | null)[] =
		posts.length > 0 ? posts : Array.from({ length: Math.min(postCount, 3) }, () => null)
	const dateFormat = new Intl.DateTimeFormat(platform.language, { dateStyle: "medium" })

	return (
		<section className="px-6 pt-16 pb-0 sm:pt-20 lg:px-8">
			<div className={layout === "list" ? "mx-auto max-w-4xl" : "mx-auto max-w-7xl"}>
				{title && (
					<h2 className="text-landing-foreground mb-12 text-center text-3xl font-bold tracking-tight sm:text-4xl">
						{title}
					</h2>
				)}
				<div className={layout === "list" ? "flex flex-col gap-6" : "grid gap-6 sm:grid-cols-2 lg:grid-cols-3"}>
					{items.map((post, index) => (
						<article
							key={post?.id ?? index}
							className={cn(
								"border-landing-border bg-landing-surface flex overflow-hidden rounded-lg border shadow-sm",
								layout === "list" ? "flex-col sm:flex-row" : "flex-col",
							)}
						>
							{post === null || post.coverImageUrl ? (
								<div
									className={cn(
										"from-landing-surface-muted to-landing-border flex aspect-video w-full items-center justify-center bg-linear-to-br",
										layout === "list" && "sm:w-64 sm:shrink-0",
									)}
								>
									{post?.coverImageUrl ? (
										<img
											src={post.coverImageUrl}
											alt={post.coverImageAlt ?? ""}
											className="size-full object-cover"
										/>
									) : (
										<IconArticle className="text-landing-foreground-subtle size-12" />
									)}
								</div>
							) : null}
							<div className="min-w-0 flex-1 p-6">
								<div className="text-landing-foreground-subtle mb-3 flex flex-wrap items-center gap-2 text-sm">
									<span>
										{post?.publishDate ? dateFormat.format(post.publishDate) : "Publish date"}
									</span>
									{post?.tags.slice(0, 2).map(tag => (
										<span
											key={tag}
											className="bg-landing-surface-muted text-landing-foreground-muted rounded-full px-2.5 py-1 text-xs font-medium"
										>
											{tag}
										</span>
									))}
								</div>
								<h3 className="text-landing-foreground text-xl font-semibold tracking-tight">
									{post?.title ?? `Blog post ${index + 1}`}
								</h3>
								<p className="text-landing-foreground-muted mt-3 line-clamp-2 text-sm leading-6">
									{post === null
										? "Your newest published blog posts will appear here"
										: (post.excerpt ?? post.metaDescription)}
								</p>
							</div>
						</article>
					))}
				</div>
				<div className="mt-8 flex justify-end">
					<span className="text-landing-foreground-muted inline-flex items-center gap-1.5 text-sm font-semibold">
						All articles
						<IconArrowRight className="size-4" />
					</span>
				</div>
			</div>
		</section>
	)
}

export default BlogPostsBlockPreview
